import React, { useMemo } from "react";
import type { U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import { U2000_AGING_BUCKETS } from "../../../u2000Core/constants/U2000AgingBuckets";
import {
  u3125ArAgingTotalsForBucket,
  u3125FormatArAgingProjectLabel,
  type U3125ArAgingCustomerDetailReport,
  type U3125ArAgingInvoiceLine,
  type U3125ArAgingProjectRow,
} from "../../U3125Service";
import {
  U3125ArAgingBucketMetricRows,
  U3125ArAgingBucketTableHeader,
} from "./U3125ArAgingBucketTable";
import U3125ArAgingInvoiceBucketRow from "./U3125ArAgingInvoiceBucketRow";

export interface U3125ArAgingCustomerExpandedTableProps {
  customerDetail: U3125ArAgingCustomerDetailReport;
  customerLabel: string;
  selectedBucket?: U2000AgingBucket | null;
  projectFilterId?: string | null;
}

interface U3125ArAgingProjectInvoiceGroup {
  projectRow: U3125ArAgingProjectRow | null;
  projectLabel: string;
  invoices: U3125ArAgingInvoiceLine[];
}

function buildProjectInvoiceGroups(
  customerDetail: U3125ArAgingCustomerDetailReport,
  projectFilterId: string | null | undefined,
): U3125ArAgingProjectInvoiceGroup[] {
  const invoices = customerDetail.invoices ?? [];
  const projects = customerDetail.projects ?? [];
  const invoicesByProject = new Map<string, U3125ArAgingInvoiceLine[]>();

  for (const inv of invoices) {
    const key = inv.projectId || "";
    const list = invoicesByProject.get(key) ?? [];
    list.push(inv);
    invoicesByProject.set(key, list);
  }

  const groups: U3125ArAgingProjectInvoiceGroup[] = [];

  for (const pr of projects) {
    const projectId = pr.project.id;
    if (projectFilterId && projectId !== projectFilterId) continue;
    const projectInvoices = invoicesByProject.get(projectId) ?? [];
    if (!projectInvoices.length) continue;
    groups.push({
      projectRow: pr,
      projectLabel: u3125FormatArAgingProjectLabel(pr.project),
      invoices: projectInvoices,
    });
    invoicesByProject.delete(projectId);
  }

  for (const [projectId, projectInvoices] of invoicesByProject) {
    if (!projectInvoices.length) continue;
    if (projectFilterId && projectId !== projectFilterId) continue;
    groups.push({
      projectRow: null,
      projectLabel: projectId || "—",
      invoices: projectInvoices,
    });
  }

  return groups;
}

const U3125ArAgingCustomerExpandedTable: React.FC<U3125ArAgingCustomerExpandedTableProps> = ({
  customerDetail,
  customerLabel,
  selectedBucket = null,
  projectFilterId = null,
}) => {
  const customerTotals = selectedBucket
    ? u3125ArAgingTotalsForBucket(customerDetail.buckets, selectedBucket)
    : customerDetail.totals;

  const projectGroups = useMemo(
    () => buildProjectInvoiceGroups(customerDetail, projectFilterId),
    [customerDetail, projectFilterId],
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-160 text-xs">
        <U3125ArAgingBucketTableHeader
          selectedBucket={selectedBucket}
          alignPeriodWithAmountLineCount
        />
        <tbody>
          <U3125ArAgingBucketMetricRows
            buckets={customerDetail.buckets}
            totals={customerTotals}
            selectedBucket={selectedBucket}
            variant="compact"
            layout="singleRow"
            countLabel={customerLabel}
            countLabelClassName="bg-gray-50 font-semibold text-gray-900"
          />
          {projectGroups.map((group, groupIndex) => {
            const projectTotals = group.projectRow
              ? selectedBucket
                ? u3125ArAgingTotalsForBucket(group.projectRow.buckets, selectedBucket)
                : group.projectRow.totals
              : null;
            const projectBuckets = group.projectRow?.buckets ?? [];

            return (
              <React.Fragment key={group.projectRow?.project.id ?? `orphan-${group.projectLabel}`}>
                {group.projectRow && projectTotals ? (
                  <U3125ArAgingBucketMetricRows
                    buckets={projectBuckets}
                    totals={projectTotals}
                    selectedBucket={selectedBucket}
                    variant="compact"
                    layout="singleRow"
                    countLabel={group.projectLabel}
                    countLabelClassName="pl-3 font-medium text-gray-700 border-l-2 border-gray-200"
                    firstDetailRow={groupIndex === 0}
                  />
                ) : (
                  <tr className={groupIndex === 0 ? "border-t-2 border-gray-200" : "border-t border-gray-100"}>
                    <td
                      colSpan={U2000_AGING_BUCKETS.length + 2}
                      className="py-0.5 pr-2 pl-3 font-medium text-gray-700 border-l-2 border-gray-200 whitespace-nowrap"
                    >
                      {group.projectLabel}
                    </td>
                  </tr>
                )}
                {group.invoices.map((inv) => (
                  <U3125ArAgingInvoiceBucketRow
                    key={inv.invoiceId}
                    invoice={inv}
                    selectedBucket={selectedBucket}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default U3125ArAgingCustomerExpandedTable;
